import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Boxes, Plus, X } from 'lucide-react';

import { lotsAPI } from '../../lib/lotsApi';
import { STATUTS_LOT } from '../../lib/lotsFiltres';
import { formatDate, formatPrixUnitaire } from '../../lib/formats';
import { libelle } from '../../lib/libelles';
import { messageErreur } from '../../lib/erreurs';
import { toast } from '../../components/ui/sonner';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { usePagination } from '../../hooks/usePagination';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { useTransporteursLots } from '../../hooks/useTransporteursLots';
import { PageHeader } from '../../components/ui/page-header';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { StatutBadge } from '../../components/ui/StatutBadge';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import LotDialog from '../../components/lots/LotDialog';
import { ChampRecherche } from '../../components/lots/lotsUi';
import { produitsDuLot } from '../../lib/lotsProduits';

const TOUS = 'tous';

/** Les lots attribués : un lot par ligne, avec ses compteurs. */
const LotsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { peutEcrire, voitArgent } = useDroitsLots();
  const { options: societes } = useTransporteursLots();

  const statut = STATUTS_LOT.includes((searchParams.get('statut') || '').toUpperCase())
    ? searchParams.get('statut').toUpperCase()
    : '';
  const transporteur = (searchParams.get('transporteur') || '').trim();
  const q = (searchParams.get('q') || '').trim();

  const [recherche, setRecherche] = useState(q);
  const rechercheDifferee = useDebouncedValue(recherche);
  const [lots, setLots] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [dialogue, setDialogue] = useState(false);

  const changer = useCallback(
    (changements) =>
      setSearchParams(
        (precedent) => {
          const suivant = new URLSearchParams(precedent);
          Object.entries(changements).forEach(([cle, valeur]) => {
            if (!valeur) suivant.delete(cle);
            else suivant.set(cle, valeur);
          });
          suivant.delete('page');
          return suivant;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  // La recherche part au serveur une fois la frappe posée.
  useEffect(() => {
    if (rechercheDifferee.trim() === q) return;
    changer({ q: rechercheDifferee.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheDifferee]);

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      if (!silencieux) setChargement(true);
      try {
        setLots(await lotsAPI.list({ status: statut, transporter_id: transporteur, q }));
        setErreur(false);
      } catch (err) {
        console.error('Failed to load lots:', err);
        setErreur(true);
        toast.error(messageErreur(err, 'Impossible de charger les lots'));
      } finally {
        setChargement(false);
      }
    },
    [statut, transporteur, q],
  );

  useEffect(() => {
    charger();
  }, [charger]);

  useRafraichirSur(['lots', 'trajets'], charger);

  const filtresActifs = [statut, transporteur, q].filter(Boolean).length;
  const pagination = usePagination(lots, { reinitialiser: `${statut}|${transporteur}|${q}` });

  const optionsTransporteurs = useMemo(
    () => [{ value: '', label: 'Tous les transporteurs' }, ...societes],
    [societes],
  );

  const effacer = () => {
    setRecherche('');
    changer({ statut: '', transporteur: '', q: '' });
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="lots-liste">
      <PageHeader
        eyebrow="Lots attribués"
        title="Lots"
        stats={[{ label: filtresActifs ? 'Lots trouvés' : 'Lots', valeur: lots.length, testid: 'lots-total' }]}
        actions={
          peutEcrire ? (
            <Button onClick={() => setDialogue(true)} data-testid="lots-nouveau">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau lot
            </Button>
          ) : null
        }
      />

      {/* Recherche étroite, filtres à sa droite. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="lots-filtres">
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Référence, transporteur…"
          label="Rechercher un lot"
          testid="lots-recherche"
        />
        <Select value={statut || TOUS} onValueChange={(valeur) => changer({ statut: valeur === TOUS ? '' : valeur })}>
          <SelectTrigger className="input-base w-40" aria-label="État du lot" data-testid="lots-filtre-statut">
            <SelectValue placeholder="État du lot" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TOUS}>État : tous</SelectItem>
            {STATUTS_LOT.map((code) => (
              <SelectItem key={code} value={code}>
                {libelle('lot_statut', code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SearchableSelect
          className="input-base w-52"
          data-testid="lots-filtre-transporteur"
          aria-label="Transporteur"
          placeholder="Transporteur"
          searchPlaceholder="Rechercher un transporteur…"
          emptyMessage="Aucun transporteur trouvé"
          value={transporteur}
          onValueChange={(valeur) => changer({ transporteur: valeur })}
          options={optionsTransporteurs}
        />
        {filtresActifs > 0 ? (
          <Button variant="ghost" size="sm" onClick={effacer} data-testid="lots-effacer-filtres">
            <X className="h-4 w-4" aria-hidden="true" />
            Effacer les filtres
          </Button>
        ) : null}
      </div>

      {chargement && lots.length === 0 ? (
        <LoadingState shape="table" />
      ) : erreur && lots.length === 0 ? (
        <EmptyState
          variant="error"
          title="Les lots n’ont pas pu être chargés"
          description="Réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={() => charger()}
        />
      ) : lots.length === 0 ? (
        <EmptyState
          variant={filtresActifs ? 'filtered' : 'empty'}
          icon={Boxes}
          title={filtresActifs ? 'Aucun résultat' : 'Aucun lot attribué'}
          description={filtresActifs ? 'Aucun lot ne correspond aux filtres actifs.' : undefined}
          aide={
            filtresActifs
              ? undefined
              : 'Un lot rassemble les camions chargés vendus par un transporteur partenaire, à un prix au litre unique.'
          }
          actionLabel={!filtresActifs && peutEcrire ? 'Créer un lot' : undefined}
          onAction={!filtresActifs && peutEcrire ? () => setDialogue(true) : undefined}
        />
      ) : (
        <Card className="card-base">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Transporteur</TableHead>
                  <TableHead>Produit</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Attribués</TableHead>
                  <TableHead className="text-right">Confirmés</TableHead>
                  <TableHead className="text-right">Reste</TableHead>
                  {voitArgent ? <TableHead className="text-right">Prix au litre</TableHead> : null}
                  <TableHead>État</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((lot) => {
                  const avancement = lot.progress || {};
                  return (
                    <TableRow
                      key={lot.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/lots/${lot.id}`)}
                      data-testid={`lot-row-${lot.id}`}
                    >
                      <TableCell className="whitespace-nowrap font-mono font-semibold">{lot.reference}</TableCell>
                      <TableCell>{lot.transporter_name || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {produitsDuLot(lot)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(lot.attributed_at)}</TableCell>
                      <TableCell className="whitespace-nowrap text-right tabular-nums">
                        {avancement.attributed || 0}
                        {lot.promised_trucks ? <span className="text-muted-foreground"> / {lot.promised_trucks}</span> : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{avancement.confirmed || 0}</TableCell>
                      <TableCell className="text-right tabular-nums">{avancement.unconfirmed || 0}</TableCell>
                      {voitArgent ? (
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {lot.price_per_liter === null || lot.price_per_liter === undefined
                            ? '—'
                            : formatPrixUnitaire(lot.price_per_liter, lot.currency)}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        <StatutBadge domaine="lot_statut" code={lot.status} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {lots.length > 0 ? <BarrePagination pagination={pagination} nom="lots" /> : null}

      {peutEcrire ? (
        <LotDialog
          open={dialogue}
          onOpenChange={setDialogue}
          voitArgent={voitArgent}
          onSaved={() => charger({ silencieux: true })}
        />
      ) : null}
    </div>
  );
};

export default LotsListPage;
